/*
 * Tykables PWA install logic
 * -----------------------------------------------------------------------------
 * - Registers the service worker at window.tykPwaConfig.swPath (root scope).
 * - Captures the Chrome/Android `beforeinstallprompt` event and defers firing.
 * - Shows a bottom-sheet prompt only after an engagement signal:
 *     * 2nd session OR
 *     * item added to cart (observed via fetch to /cart/add + cart:added CE)
 * - On iOS Safari, shows a variant sheet with Share-sheet instructions.
 * - Dismissal is remembered for 30 days in localStorage.
 * - Does nothing if already installed (display-mode: standalone).
 * - Emits `pwa:install-prompt-resolved`, `pwa:installed`, `pwa:already-installed`
 *   custom events for other listeners (e.g. OneSignal) to hook into.
 */
(function () {
  'use strict';

  var CONFIG = window.tykPwaConfig || {};
  if (CONFIG.enabled === false) return;

  var STORAGE_KEY = 'tyk_pwa_state';
  var SESSION_FLAG = 'tyk_pwa_session_counted';
  var DISMISS_WINDOW_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
  var NOW = Date.now();

  function readState() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    } catch (e) {
      return {};
    }
  }

  function writeState(state) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      /* quota / private mode — non-fatal */
    }
  }

  function bumpSession() {
    try {
      if (!sessionStorage.getItem(SESSION_FLAG)) {
        var s = readState();
        s.sessions = (s.sessions || 0) + 1;
        s.lastSessionAt = NOW;
        writeState(s);
        sessionStorage.setItem(SESSION_FLAG, '1');
      }
    } catch (e) {
      /* private-mode fallback */
    }
  }
  bumpSession();

  function isStandalone() {
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
    // iOS
    if (typeof window.navigator.standalone !== 'undefined' && window.navigator.standalone === true) return true;
    return false;
  }

  function isIOSSafari() {
    var ua = navigator.userAgent || '';
    var iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    // iPadOS masquerades as MacIntel
    var iPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
    if (!iOS && !iPadOS) return false;
    // Exclude in-app browsers (FB, Instagram, etc.) where Add-to-Home-Screen doesn't work
    if (/FBAN|FBAV|Instagram|Line|Twitter/i.test(ua)) return false;
    // Exclude Chrome/Firefox/Edge on iOS (they can't add to home screen either)
    if (/CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua)) return false;
    return true;
  }

  function isDismissed() {
    var s = readState();
    if (!s.dismissedAt) return false;
    return NOW - s.dismissedAt < DISMISS_WINDOW_MS;
  }

  function markDismissed() {
    var s = readState();
    s.dismissedAt = NOW;
    writeState(s);
  }

  function engagementMet() {
    var s = readState();
    return (s.sessions || 0) >= 2 || s.addedToCart === true;
  }

  // ---------------------------------------------------------------------------
  // Service Worker registration
  // ---------------------------------------------------------------------------
  function registerSW() {
    if (!('serviceWorker' in navigator)) return;
    var path = CONFIG.swPath || '/apps/pwa/sw.js';
    var scope = CONFIG.scope || '/';

    // Pre-flight check: if the SW endpoint isn't reachable yet (e.g. the
    // Shopify App Proxy + Cloudflare Worker backend hasn't been deployed),
    // silently skip registration so the console stays clean in dev/preview.
    fetch(path, { method: 'HEAD', credentials: 'same-origin' })
      .then(function (res) {
        if (!res || !res.ok) {
          // eslint-disable-next-line no-console
          if (window.tykPwaConfig && window.tykPwaConfig.debug) {
            console.debug('[tyk-pwa] SW endpoint not reachable (' + (res && res.status) + '); skipping registration.');
          }
          return null;
        }
        var ct = res.headers && res.headers.get && res.headers.get('content-type') || '';
        if (ct && ct.indexOf('javascript') === -1 && ct.indexOf('text/plain') === -1) {
          // Content-Type is wrong — likely an HTML 404 page that returned 200
          if (window.tykPwaConfig && window.tykPwaConfig.debug) {
            console.debug('[tyk-pwa] SW endpoint returned non-JS content-type (' + ct + '); skipping.');
          }
          return null;
        }
        return navigator.serviceWorker.register(path, { scope: scope });
      })
      .then(function (reg) {
        if (!reg || !reg.addEventListener) return;
        reg.addEventListener('updatefound', function () {
          var nw = reg.installing;
          if (!nw) return;
          nw.addEventListener('statechange', function () {
            if (nw.state === 'installed' && navigator.serviceWorker.controller) {
              nw.postMessage({ type: 'TYK_SKIP_WAITING' });
            }
          });
        });
      })
      .catch(function (err) {
        // eslint-disable-next-line no-console
        if (window.tykPwaConfig && window.tykPwaConfig.debug) {
          console.debug('[tyk-pwa] SW registration skipped:', err && err.message);
        }
      });
  }
  registerSW();

  // ---------------------------------------------------------------------------
  // Already installed? Announce + exit.
  // ---------------------------------------------------------------------------
  if (isStandalone()) {
    document.dispatchEvent(new CustomEvent('pwa:already-installed'));
    return;
  }

  // ---------------------------------------------------------------------------
  // Add-to-cart signal (fetch interception + pubsub listener)
  // ---------------------------------------------------------------------------
  function markAddedToCart() {
    var s = readState();
    if (s.addedToCart === true) return;
    s.addedToCart = true;
    s.firstCartAt = NOW;
    writeState(s);
    setTimeout(maybeShow, 1500); // let the Shopify cart drawer finish animating
  }

  document.addEventListener('cart:added', markAddedToCart);

  if (typeof window.fetch === 'function') {
    var origFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var isCartAdd = url && url.indexOf('/cart/add') !== -1;
      var result = origFetch.apply(this, arguments);
      if (isCartAdd) {
        result.then(function (res) {
          if (res && res.ok) markAddedToCart();
        }).catch(function () { /* swallow */ });
      }
      return result;
    };
  }

  // ---------------------------------------------------------------------------
  // beforeinstallprompt capture (Chrome/Android/Desktop Chrome/Edge)
  // ---------------------------------------------------------------------------
  var deferredPrompt = null;

  window.addEventListener('beforeinstallprompt', function (e) {
    e.preventDefault();
    deferredPrompt = e;
    if (document.readyState !== 'loading') maybeShow();
  });

  window.addEventListener('appinstalled', function () {
    var s = readState();
    s.installed = true;
    s.installedAt = NOW;
    writeState(s);
    hideSheet('android');
    hideSheet('ios');
    deferredPrompt = null;
    document.dispatchEvent(new CustomEvent('pwa:installed'));
  });

  // ---------------------------------------------------------------------------
  // Sheet show/hide
  // ---------------------------------------------------------------------------
  function showSheet(variant) {
    var el = document.getElementById('tyk-pwa-sheet-' + variant);
    if (!el) return;
    if (el.classList.contains('is-visible')) return;
    el.classList.add('is-visible');
    el.removeAttribute('inert');
    // Focus the primary action for keyboard / screen-reader users.
    var firstBtn = el.querySelector('button');
    if (firstBtn) setTimeout(function () { firstBtn.focus(); }, 350);
    document.dispatchEvent(new CustomEvent('pwa:install-prompt-shown', { detail: { variant: variant } }));
  }

  function hideSheet(variant) {
    var el = document.getElementById('tyk-pwa-sheet-' + variant);
    if (!el) return;
    el.classList.remove('is-visible');
    el.setAttribute('inert', '');
  }

  function maybeShow() {
    if (isStandalone()) return;
    if (isDismissed()) return;
    if (!engagementMet()) return;
    if (deferredPrompt) {
      showSheet('android');
    } else if (isIOSSafari()) {
      showSheet('ios');
    }
  }

  function acceptAndroidPrompt() {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    deferredPrompt.userChoice.then(function (choice) {
      var outcome = (choice && choice.outcome) || 'dismissed';
      deferredPrompt = null;
      hideSheet('android');
      if (outcome !== 'accepted') markDismissed();
      document.dispatchEvent(new CustomEvent('pwa:install-prompt-resolved', {
        detail: { outcome: outcome, platform: 'android' }
      }));
    }).catch(function () {
      hideSheet('android');
      document.dispatchEvent(new CustomEvent('pwa:install-prompt-resolved', {
        detail: { outcome: 'dismissed', platform: 'android' }
      }));
    });
  }

  // ---------------------------------------------------------------------------
  // DOM wiring
  // ---------------------------------------------------------------------------
  function wire() {
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-pwa-install]'),
      function (btn) { btn.addEventListener('click', acceptAndroidPrompt); }
    );
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-pwa-dismiss]'),
      function (btn) {
        btn.addEventListener('click', function (e) {
          var variant = e.currentTarget.getAttribute('data-pwa-dismiss');
          markDismissed();
          hideSheet(variant);
          document.dispatchEvent(new CustomEvent('pwa:install-prompt-resolved', {
            detail: { outcome: 'dismissed', platform: variant }
          }));
        });
      }
    );
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-pwa-ios-ok]'),
      function (btn) {
        btn.addEventListener('click', function () {
          hideSheet('ios');
          // Treat acknowledgment as prompt resolution so push flow can kick in.
          document.dispatchEvent(new CustomEvent('pwa:install-prompt-resolved', {
            detail: { outcome: 'ios-instructed', platform: 'ios' }
          }));
        });
      }
    );

    // First pass after DOM is ready.
    maybeShow();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wire);
  } else {
    wire();
  }
})();
